import { COURSE_DURATIONS } from '@/config/courseDurations';
import SelectiveSchoolsFinder from '@/components/SelectiveSchoolsFinder';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import VideoPlayer from '@/components/VideoPlayer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCoursePricing } from '@/hooks/useCoursePricing';
import { Target, Users, Clock, BookOpen, CheckCircle, ArrowRight, Star, Award, Brain, Calculator, FileText, Lightbulb, Trophy, PlayCircle, Image, Upload, DollarSign, Calendar, PenTool, Zap } from 'lucide-react';
import { BreadcrumbSchema, generateProgramBreadcrumbs, SelectiveProgramVideoSchema, ProgramSpeakableSchema } from '@/components/seo';
import { buildCourseSchema, offersFromPricing, TUTORING_PROVIDER } from '@/data/courses';
const SelectiveProgram = () => {
  // Fetch Selective pricing data from Supabase
  const {
    pricing: sptcPricing
  } = useCoursePricing('NSW', 'sptc');
  const {
    pricing: selectivePricing
  } = useCoursePricing('NSW', 'selective');

  // Helper function to get pricing values
  const getWeeklyPrice = () => {
    if (sptcPricing.length > 0) {
      return sptcPricing[0].price_per_session;
    }
    return 77; // fallback
  };
  const getTermPrice = () => {
    if (sptcPricing.length > 0) {
      return sptcPricing[0].price_per_session * sptcPricing[0].total_sessions;
    }
    return 770; // fallback
  };
  const getBooksCost = () => {
    if (selectivePricing.length > 0) {
      return selectivePricing[0].books_cost;
    }
    return 77; // fallback
  };
  const selectiveTestAreas = [{
    name: 'Reading Test',
    icon: BookOpen,
    description: 'Reading comprehension skills, text analysis, and interpretation of written material',
    questions: '16',
    questionsNote: '',
    minutes: '40',
    type: 'Multiple-choice',
    weighting: '25%'
  }, {
    name: 'Mathematical Reasoning Test',
    icon: Calculator,
    description: 'Advanced mathematical concepts, numerical patterns, and quantitative reasoning',
    questions: '40',
    questionsNote: '',
    minutes: '35',
    type: 'Multiple-choice',
    weighting: '25%'
  }, {
    name: 'Thinking Skills Test',
    icon: Brain,
    description: 'Logical reasoning, problem-solving patterns, and critical thinking abilities',
    questions: '40',
    questionsNote: '',
    minutes: '40',
    type: 'Multiple-choice',
    weighting: '25%'
  }, {
    name: 'Writing Test',
    icon: PenTool,
    description: 'Written expression, creative and persuasive writing, and language skills',
    questions: '1',
    questionsNote: '',
    minutes: '30',
    type: 'Open response',
    weighting: '25%'
  }];
  const schoololaAdvantages = ['Expert NSW curriculum teachers with Selective specialization', 'Small class sizes (8-10 students) for personalized attention', 'Comprehensive practice tests and mock examinations', 'Strategic time management and exam techniques', 'Regular progress monitoring and feedback', 'Stress management and confidence building', 'Access to exclusive Selective practice materials', 'Technology-focused learning with mobile apps', 'Free homework support and video resources'];
  const whyChooseSchoolola = [{
    title: 'Expert Teachers',
    description: 'Qualified NSW curriculum teachers specialising in Selective preparation methodologies',
    icon: Users
  }, {
    title: 'Comprehensive Approach',
    description: 'Complete coverage of all Selective test areas with strategic exam preparation',
    icon: Target
  }];
  const blogTopics = [{
    title: 'How to Use the NSW Selective Score Calculator: A Parent\'s Guide',
    link: '/blog/how-to-use-selective-calculator',
    description: 'Step-by-step guide for parents to use our free calculator and plan their child\'s preparation',
    featured: true
  }, {
    title: 'NSW Selective School Score Calculator: Complete Guide 2025',
    link: '/blog/selective-score-calculator-guide',
    description: 'Use our free calculator to predict your child\'s chances and understand selective school cutoffs'
  }, {
    title: 'Complete Guide to Selective School Test 2024',
    link: '/selective-program',
    description: 'Expert tips and strategies for Selective test success'
  }, {
    title: 'Mastering Thinking Skills for Selective Test',
    link: '/selective-program',
    description: 'Expert tips and strategies for Selective test success'
  }, {
    title: 'Mathematical Reasoning Strategies Explained',
    link: '/selective-program',
    description: 'Expert tips and strategies for Selective test success'
  }, {
    title: 'Writing Excellence for Selective Success',
    link: '/selective-program',
    description: 'Expert tips and strategies for Selective test success'
  }, {
    title: 'Reading Comprehension Tips for Selective',
    link: '/selective-program',
    description: 'Expert tips and strategies for Selective test success'
  }];
  return <div className="min-h-screen bg-white">
      <Helmet>
        <title>NSW Selective School Test Preparation | Selective High School Tutoring | Schoolola</title>
        <meta name="description" content="Expert NSW Selective School test preparation. Specialised tutoring for Year 6 students. Small classes. Book free assessment today." />
        <meta name="keywords" content="Selective school test preparation, Selective high school tutoring, NSW selective coaching, selective test practice, selective school placement, selective exam preparation" />
        <meta property="og:title" content="NSW Selective School Test Preparation | Selective High School Tutoring | Schoolola" />
        <meta property="og:description" content="Expert NSW Selective School test preparation. Specialised tutoring for Year 6 students." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(buildCourseSchema('selective-tmrw', { workload: COURSE_DURATIONS.selectiveTmrw.iso, schedule: COURSE_DURATIONS.selectiveTmrw.schedule, offers: offersFromPricing(selectivePricing), provider: TUTORING_PROVIDER }))}</script>
      </Helmet>
      
      <BreadcrumbSchema items={generateProgramBreadcrumbs("Selective TMRW™ Course", "selective-school")} />
      <SelectiveProgramVideoSchema />
      <ProgramSpeakableSchema programName="NSW Selective School Test Preparation" slug="selective-school" />
      
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-accent-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <Badge className="bg-accent text-white px-6 py-3 text-lg rounded-2xl">
              NSW Selective School Programme
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-heading font-bold text-gray-900">
              Master the Selective Test with NSW's Leading Experts
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Comprehensive Selective School preparation designed by NSW education specialists. 
              Our proven methodology has helped thousands of students secure their place in Selective Schools across NSW.
            </p>
            
            {/* Video Section */}
            <div className="max-w-2xl mx-auto mt-8">
              <VideoPlayer src="/selective-program-video.mp4" className="shadow-lg" autoplay={true} controls={false} muted={true} loop={true} />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-assessment">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 rounded-2xl">
                  Book Free Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="text-lg px-8 rounded-2xl">
                  Enquire About Smaller Groups
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is the Selective Test Section - Compact Design */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <Badge className="bg-primary/10 text-primary px-4 py-2 rounded-2xl">
                NSW Selective Test Overview
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Understanding the <span className="text-primary">Selective School Test</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A competitive examination for Year 6 students designed to identify candidates for selective high schools.{' '}
                <Link to="/nsw-selective-calculator" className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                  Check your child's chances with our free Selective Score Calculator
                  <Calculator className="h-4 w-4" />
                </Link>
              </p>
            </div>

            {/* Test Areas - Desktop Grid, Mobile Carousel */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {selectiveTestAreas.map((area, index) => <Card key={index} className="relative border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <area.icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-gray-900 text-lg leading-tight">
                          {area.name}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {area.description}
                    </p>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-primary/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-primary">{area.questions}</div>
                        <div className="text-xs text-gray-600">Questions</div>
                      </div>
                      <div className="bg-accent/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-accent">{area.minutes}</div>
                        <div className="text-xs text-gray-600">Minutes</div>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Type:</span>
                        <span className="font-medium text-gray-800">{area.type}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Weight:</span>
                        <span className="font-medium text-gray-800">{area.weighting}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>

            {/* Mobile Carousel */}
            <div className="md:hidden">
              <Carousel className="w-full" plugins={[Autoplay({
              delay: 3000
            }) as any]} opts={{
              align: "start",
              loop: true
            }}>
                <CarouselContent className="-ml-4">
                  {selectiveTestAreas.map((area, index) => <CarouselItem key={index} className="pl-4">
                      <Card className="relative border-0 shadow-lg">
                        <CardContent className="p-6">
                          {/* Header */}
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="h-12 w-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                              <area.icon className="h-6 w-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-heading font-bold text-gray-900 text-lg leading-tight">
                                {area.name}
                              </h3>
                            </div>
                          </div>

                          {/* Description */}
                          <p className="text-gray-600 text-sm leading-relaxed mb-4">
                            {area.description}
                          </p>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-primary/5 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-primary">{area.questions}</div>
                              <div className="text-xs text-gray-600">Questions</div>
                            </div>
                            <div className="bg-accent/5 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-accent">{area.minutes}</div>
                              <div className="text-xs text-gray-600">Minutes</div>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Type:</span>
                              <span className="font-medium text-gray-800">{area.type}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Weight:</span>
                              <span className="font-medium text-gray-800">{area.weighting}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </CarouselItem>)}
                </CarouselContent>
              </Carousel>
            </div>

            {/* Bottom Summary Bar */}
            <div className="mt-12 bg-white rounded-2xl shadow-lg p-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-primary">97</div>
                  <div className="text-sm text-gray-600">Total Questions</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-accent">2.5</div>
                  <div className="text-sm text-gray-600">Hours</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-primary">4</div>
                  <div className="text-sm text-gray-600">Test Areas</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-accent">25%</div>
                  <div className="text-sm text-gray-600">Each Section</div>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <Link to="/nsw-selective-calculator">
                    <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white">
                      <Calculator className="h-4 w-4 mr-2" />
                      Score Calculator
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schoolola's Premium Courses */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Schoolola's Premium Selective Courses
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose from flexible payment options and scheduling - pay per term or weekly membership
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Selective TMRW Course */}
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-primary to-primary-600 text-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="bg-accent text-white mb-2">BESTSELLER</Badge>
                      <CardTitle className="text-2xl font-heading">
                        Selective TMRW™
                      </CardTitle>
                      <p className="text-primary-100 mt-2">The Tomorrow Method for Selective Success</p>
                    </div>
                    <Brain className="h-12 w-12 text-primary-100" />
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-semibold">{COURSE_DURATIONS.selectiveTmrw.page}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-primary" />
                      <span className="font-semibold">10 weeks per term</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="h-5 w-5 text-primary" />
                      <span className="font-semibold">8-10 students per class</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Flexible Payment Options:</h4>
                    <div className="bg-primary-50 rounded-lg p-4 mb-4">
                      <h5 className="font-semibold text-primary mb-2">Option 1: Term Payment</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Term Rate (incl. GST)</span>
                          <span className="font-bold text-primary text-lg">${getTermPrice()}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>Course Books (extra)</span>
                          <span className="font-semibold text-accent">${getBooksCost()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-accent-50 rounded-lg p-4">
                      <h5 className="font-semibold text-accent mb-2">Option 2: Weekly Membership</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Weekly Rate (incl. GST)</span>
                          <span className="font-bold text-accent text-lg">${getWeeklyPrice()}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>Course Books (extra)</span>
                          <span className="font-semibold text-accent">${getBooksCost()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-2">Scheduling Options:</h5>
                    <ul className="text-sm space-y-1 text-gray-700 mb-3">
                      <li>• Two 2-hour sessions per week</li>
                      <li>• One 4-hour Saturday session</li>
                    </ul>
                    <h5 className="font-semibold text-gray-900 mb-2">What's Included:</h5>
                    <ul className="text-sm space-y-1 text-gray-700">
                      <li>• Thinking Skills for 1 hour</li>
                      <li>• Reading Comprehension for 1 hour</li>
                      <li>• Writing Skills for 1 hour</li>
                      <li>• Mathematics for 1 hour</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* SPTC Course */}
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-accent to-orange-500 text-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="bg-primary text-white mb-2">TEST PACK COURSE</Badge>
                      <CardTitle className="text-2xl font-heading">
                        SPTC™
                      </CardTitle>
                      <p className="text-orange-100 mt-2">Selective Practice Test Course</p>
                    </div>
                    <FileText className="h-12 w-12 text-orange-100" />
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-accent" />
                      <span className="font-semibold">2.5 hours per session, 1 session a week</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-accent" />
                      <span className="font-semibold">10 weeks per term</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="h-5 w-5 text-accent" />
                      <span className="font-semibold">8-10 students per class</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Flexible Payment Options:</h4>
                    <div className="bg-accent-50 rounded-lg p-4 mb-4">
                      <h5 className="font-semibold text-accent mb-2">Option 1: Term Payment</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Term Rate (incl. GST)</span>
                          <span className="font-bold text-accent text-lg">${getTermPrice()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-primary-50 rounded-lg p-4">
                      <h5 className="font-semibold text-primary mb-2">Option 2: Weekly Membership</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Weekly Rate (incl. GST)</span>
                          <span className="font-bold text-primary text-lg">${getWeeklyPrice()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-2">What's Included:</h5>
                    <ul className="text-sm space-y-1 text-gray-700">
                      <li>• One test per week plus explanations delivered within session</li>
                      <li>• All questions will be covered</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12 p-6 bg-blue-50 rounded-2xl">
              <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
                Want Smaller Group Classes?
              </h3>
              <p className="text-gray-600 mb-4">
                Contact your local Schoolola centre to enquire about smaller group options and availability
              </p>
              <Link to="/contact">
                <Button className="bg-primary hover:bg-primary/90">
                  Enquire at Your Local Centre
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Topics Section - Carousel */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Selective Test Preparation Resources
              </h2>
              <p className="text-xl text-gray-600">
                Expert insights and strategies for Selective success
              </p>
            </div>
            
            <Carousel className="w-full" plugins={[Autoplay({
            delay: 4000
          }) as any]} opts={{
            align: "start",
            loop: true
          }}>
              <CarouselContent className="-ml-2 md:-ml-4">
                {blogTopics.map((topic, index) => <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3 h-60 flex items-center">
                    <Link to={topic.link} className="group w-full">
                      <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 h-48">
                        <CardContent className="p-6 h-full flex flex-col">
                          <div className="flex items-start space-x-3 flex-grow">
                            <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                              {index === 0 ? <Calculator className="h-6 w-6 text-primary" /> : <Brain className="h-6 w-6 text-primary" />}
                            </div>
                            <div className="flex-grow">
                              <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors leading-tight mb-3">
                                {topic.title}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {topic.description}
                              </p>
                            </div>
                          </div>
                          
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center text-primary text-sm font-medium group-hover:text-primary/80 transition-colors">
                              Read Article
                              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </CarouselItem>)}
              </CarouselContent>
              
              {/* Navigation Arrows */}
              <CarouselPrevious className="hidden md:flex -left-12 lg:-left-16" />
              <CarouselNext className="hidden md:flex -right-12 lg:-right-16" />
            </Carousel>
          </div>
        </div>
      </section>


      {/* Why Choose Schoolola */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Why Schoolola is NSW's Leading Selective Specialist
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our proven track record and expert methodology make us the trusted choice for Selective preparation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {whyChooseSchoolola.map((reason, index) => <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
                  <CardContent className="p-8 space-y-4">
                    <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                      <reason.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {reason.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            <Card className="border-0 shadow-lg bg-gradient-to-r from-primary-50 to-accent-50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6 text-center">
                  The Schoolola Advantage
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {schoololaAdvantages.map((advantage, index) => <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{advantage}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>


      {/* Score Calculator CTA Section - Creative Design */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-primary-50/30 to-accent-50/30 relative overflow-hidden">
        {/* Floating Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-64 h-64 bg-primary rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-accent rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '1s'
        }}></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-primary-300 rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '2s'
        }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Asymmetric Grid Layout */}
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              
              {/* Left Side - Large Calculator Icon Feature */}
              <div className="lg:col-span-5 flex justify-center lg:justify-end">
                <div className="relative">
                  {/* Main Calculator Circle */}
                  <div className="relative w-72 h-72 bg-gradient-to-br from-primary via-primary-600 to-accent rounded-3xl shadow-2xl flex items-center justify-center transform rotate-6 hover:rotate-0 transition-all duration-500 hover:scale-110">
                    <Calculator className="h-32 w-32 text-white" strokeWidth={1.5} />
                    
                    {/* Floating Badge */}
                    <div className="absolute -top-4 -right-4 bg-accent text-white px-6 py-3 rounded-2xl shadow-xl transform -rotate-12 animate-bounce">
                      <div className="text-2xl font-bold">FREE</div>
                    </div>
                    
                    {/* Bottom Stats */}
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white px-8 py-4 rounded-2xl shadow-xl whitespace-nowrap">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary">100%</div>
                        <div className="text-sm text-gray-600">Free Forever</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Decorative Circles */}
                  <div className="absolute -z-10 -top-8 -left-8 w-24 h-24 bg-accent/20 rounded-full"></div>
                  <div className="absolute -z-10 -bottom-12 -right-12 w-32 h-32 bg-primary/20 rounded-full"></div>
                </div>
              </div>
              
              {/* Right Side - Content */}
              <div className="lg:col-span-7 space-y-8">
                <div className="space-y-4">
                  <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg">
                    <Zap className="h-5 w-5 text-accent" />
                    <span className="font-semibold text-gray-800">Instant Predictions</span>
                  </div>
                  
                  <h2 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900 leading-tight">
                    Calculate Your Child's
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-2">
                      Selective School Chances
                    </span>
                  </h2>
                  
                  <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                    Our advanced calculator analyses scores across all NSW selective schools to give you accurate predictions
                  </p>
                </div>
                
                {/* Feature Grid */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-primary/10">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-primary to-primary-600 rounded-xl flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-heading font-bold text-gray-900">School-by-School</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Compare your chances across all 47 selective schools</p>
                  </div>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-accent/10">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center">
                        <Star className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-heading font-bold text-gray-900">Updated Data</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Based on latest 2024 cutoff scores</p>
                  </div>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-primary/10">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-primary-600 to-accent rounded-xl flex items-center justify-center">
                        <Brain className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-heading font-bold text-gray-900">Smart Analysis</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Intelligent predictions based on historical trends</p>
                  </div>
                  
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-accent/10">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-accent to-primary-600 rounded-xl flex items-center justify-center">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-heading font-bold text-gray-900">Detailed Guide</h3>
                    </div>
                    <p className="text-gray-600 text-sm">Complete guide to understanding your results</p>
                  </div>
                </div>
                
                {/* CTA Button */}
                <div className="flex justify-start pt-4">
                  <Link to="/nsw-selective-calculator">
                    <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:from-primary-600 hover:to-accent/90 text-white text-lg px-10 py-6 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                      Use Free Calculator
                      <Calculator className="ml-2 h-6 w-6" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary-600 to-accent relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Content */}
              <div className="space-y-6 text-white">
                <h2 className="text-4xl lg:text-5xl font-heading font-bold leading-tight">
                  Start Your Child's Selective Journey Today
                </h2>
                
                <p className="text-xl text-white/90 leading-relaxed">
                  Join thousands of successful students who achieved their Selective dreams with Schoolola's expert guidance
                </p>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-6 py-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-accent">500+</div>
                    <div className="text-sm text-white/80">Students</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-accent">3+</div>
                    <div className="text-sm text-white/80">Years of Experience</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-accent">10</div>
                    <div className="text-sm text-white/80">Centres</div>
                  </div>
                </div>
              </div>
              
              {/* Right: CTA Cards */}
              <div className="space-y-4">
                <Card className="border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">
                          Free Assessment
                        </h3>
                        <p className="text-gray-600 text-sm mb-4">
                          Discover your child's strengths and personalized learning path
                        </p>
                        <Link to="/book-assessment" className="w-full block">
                          <Button className="w-full bg-accent hover:bg-accent/90 rounded-2xl">
                            Book Now
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center flex-shrink-0">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">
                          Contact a Centre
                        </h3>
                        <p className="text-gray-600 text-sm mb-4">
                          Speak with our expert team about your child's journey
                        </p>
                        <Link to="/contact" className="w-full block">
                          <Button variant="secondary" className="w-full rounded-2xl">
                            Get in Touch
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Selective Schools Finder Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <SelectiveSchoolsFinder />
        </div>
      </section>

      <Footer />
    </div>;
};
export default SelectiveProgram;